import { useState, useContext, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../../api/api";
import "../Signup/Auth.css";
import { AuthContext } from "../../context/AuthContext";
import { GoogleLogin } from "@react-oauth/google";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(AuthContext);

  const [form, setForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [notVerifiedEmail, setNotVerifiedEmail] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // ✅ If coming from signup, pre-fill email and show verify banner immediately
    if (location.state?.pendingVerification && location.state?.email) {
      setForm(f => ({ ...f, email: location.state.email }));
      setNotVerifiedEmail(location.state.email);
    }
  }, [location.state]);
  
  useEffect(() => {
    let timer;
    if (cooldown > 0) timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleChange = (e) => {
    setLoginError("");
    setNotVerifiedEmail(null);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const sendTimezone = async () => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try { await api.post("/auth/set-timezone", { timezone }); } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    setNotVerifiedEmail(null);
    setLoading(true);
    try {
      const res = await api.post("/auth/login", form);
      setUser(res.data.user);
      await sendTimezone();
      navigate("/tracks");
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "EMAIL_NOT_VERIFIED") {
        setNotVerifiedEmail(data.email);
      } else {
        setLoginError(data?.message || "Invalid email or password.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (response) => {
    try {
      setLoading(true);
      const res = await api.post("/auth/google", { credential: response.credential });
      setUser(res.data.user);
      await sendTimezone();
      navigate("/tracks");
    } catch (err) {
      setLoginError(err.response?.data?.message || "Google login failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      setResetLoading(true);
      setResetMessage("");
      await api.post("/auth/forgot-password", { email: resetEmail });
      setResetMessage("If this email exists, a reset link has been sent.");
    } catch (err) {
      setResetMessage(err.response?.data?.message || "Something went wrong.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendMessage("");
      await api.post("/auth/resend-verification", { email: notVerifiedEmail });
      setResendMessage("Verification email sent!");
      setCooldown(60);
    } catch (err) {
      setResendMessage(err.response?.data?.message || "Failed to resend email.");
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="auth-card">

        {/* Logo */}
        <div className="auth-logo">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className="auth-logo-svg">
            <rect width="28" height="28" rx="7" fill="#09090b"/>
            <rect x="7" y="16" width="14" height="2.5" rx="1.25" fill="white" opacity="0.4"/>
            <rect x="7" y="12" width="10" height="2.5" rx="1.25" fill="white" opacity="0.7"/>
            <rect x="7" y="8" width="6" height="2.5" rx="1.25" fill="white"/>
            <path d="M19 9.5L21.5 12L19 14.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="auth-logo-name">learnlia</span>
        </div>

        {/* Heading */}
        <h1 className="auth-heading">Welcome back</h1>
        <p className="auth-subheading">Sign in to continue your streak.</p>

        <div className="auth-google-wrapper">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setLoginError("Google Login Failed")}
            useOneTap
            theme="filled_black"
            shape="pill"
            width="100%"
          />
        </div>

        <div className="auth-divider-container">
          <div className="auth-divider-line"></div>
          <span className="auth-divider-text">or continue with email</span>
          <div className="auth-divider-line"></div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="auth-field">
            <label className="auth-label">Email</label>
            <input
              className="auth-input"
              type="email"
              name="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-field">
            <label className="auth-label">Password</label>
            <input
              className="auth-input"
              type="password"
              name="password"
              placeholder="••••••••"
              value={form.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="auth-forgot">
            <button
              type="button"
              className="auth-forgot-btn"
              onClick={() => setShowForgot(true)}
            >
              Forgot password?
            </button>
          </div>

          {loginError && (
            <div className="auth-error">{loginError}</div>
          )}

          {notVerifiedEmail && (
            <div className="verify-box">
              <p>📩 Please verify your email first. Check your inbox and spam folder.</p>
              <button
                type="button"
                onClick={handleResendVerification}
                disabled={cooldown > 0}
                className="resend-btn"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend verification"}
              </button>
              {resendMessage && <p className="resend-message">{resendMessage}</p>}
            </div>
          )}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? <span className="spinner" /> : "Sign in"}
          </button>
        </form>

        <div className="auth-link">
          No account? <Link to="/signup">Sign up free</Link>
        </div>

      </div>

      {/* Forgot password modal */}
      {showForgot && (
        <div className="modal-overlay" onClick={() => setShowForgot(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3>Reset password</h3>
            <p>Enter your email and we'll send you a link to reset your password.</p>
            <input
              type="email"
              placeholder="you@example.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
            />
            <button
              className="modal-btn-primary"
              onClick={handleForgotPassword}
              disabled={resetLoading}
            >
              {resetLoading ? "Sending…" : "Send reset link"}
            </button>
            {resetMessage && <p className="reset-message">{resetMessage}</p>}
            <button
              className="modal-btn-ghost"
              onClick={() => { setShowForgot(false); setResetEmail(""); setResetMessage(""); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;